import { useMemo, useRef, useState } from 'react';
import { store, useAppStore } from '../store/appStore';
import { FILE_ACCEPT_DESKTOP, isAppleTouchDevice, loadMeshFile } from '../import/loaders';
import { toast } from '../ui/toast';
import { APP_NAME, APP_TAGLINE, APP_VERSION, toggleObjectsPanel, toggleSettingsPanel } from '../ui/layout';
import { IconMenu, IconRedo, IconSettings, IconUndo, IconUpload } from '../ui/icons';
import { LogoMark } from '../ui/LogoMark';
import type { ViewMode } from '../store/types';

const TABS: { id: ViewMode; label: string }[] = [
  { id: 'prepare', label: 'Prepare' },
  { id: 'preview', label: 'Preview' },
  { id: 'export', label: 'Export' },
];

export function HeaderBar({ onFiles }: { onFiles?: (files: FileList | File[]) => Promise<void> }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const viewMode = useAppStore((s) => s.viewMode);
  const settingsOpen = useAppStore((s) => s.settingsOpen);
  const settingsPanelVisible = useAppStore((s) => s.settingsPanelVisible);
  const objectsPanelVisible = useAppStore((s) => s.objectsPanelVisible);
  const theme = useAppStore((s) => s.theme);
  const canUndo = useAppStore((s) => s.canUndo);
  const canRedo = useAppStore((s) => s.canRedo);
  const [menuOpen, setMenuOpen] = useState(false);

  const ios = useMemo(() => isAppleTouchDevice(), []);
  const accept = ios ? undefined : FILE_ACCEPT_DESKTOP;
  const settingsActive = settingsOpen || settingsPanelVisible;

  async function handleFiles(files: FileList | null) {
    if (!files?.length) return;
    if (onFiles) {
      await onFiles(files);
      return;
    }
    for (const f of Array.from(files)) {
      try {
        const mesh = await loadMeshFile(f);
        store.addMesh(mesh.name, mesh.positions);
        toast(`Loaded ${mesh.name}`);
      } catch (e) {
        toast(`Failed: ${(e as Error).message}`);
      }
    }
  }

  function setMode(mode: ViewMode) {
    const patch: { viewMode: ViewMode; overhangHighlight?: boolean } = { viewMode: mode };
    if (mode === 'preview') patch.overhangHighlight = true;
    if (mode === 'prepare') patch.overhangHighlight = false;
    store.set(patch);
  }

  return (
    <header className="top-header" data-testid="toolbar">
      <div className="brand">
        <LogoMark size={24} className="brand-logo" />
        <div className="brand-text">
          <strong>{APP_NAME}</strong>
          <span className="sub">{APP_TAGLINE}</span>
        </div>
      </div>

      <nav className="mode-tabs" role="tablist" aria-label="Workspace mode">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={viewMode === t.id}
            className={viewMode === t.id ? 'active' : ''}
            data-testid={`mode-${t.id}`}
            onClick={() => setMode(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="header-actions">
        <div className="header-history" data-testid="header-history">
          <button
            type="button"
            className="header-btn icon-btn"
            data-testid="undo-btn"
            title="Undo"
            aria-label="Undo"
            disabled={!canUndo}
            onClick={() => store.undo()}
          >
            <IconUndo size={18} />
          </button>
          <button
            type="button"
            className="header-btn icon-btn"
            data-testid="redo-btn"
            title="Redo"
            aria-label="Redo"
            disabled={!canRedo}
            onClick={() => store.redo()}
          >
            <IconRedo size={18} />
          </button>
        </div>
        <button type="button" className="header-btn primary" data-testid="upload-btn" onClick={() => fileRef.current?.click()}>
          <IconUpload size={16} />
          <span>Upload</span>
        </button>
        <input
          ref={fileRef}
          type="file"
          {...(accept ? { accept } : {})}
          multiple
          hidden
          data-testid="file-input"
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          className={`header-btn icon-btn ${settingsActive ? 'active' : ''}`}
          data-testid="toggle-settings"
          title="Show or hide settings"
          aria-label="Settings"
          onClick={toggleSettingsPanel}
        >
          <IconSettings size={18} />
        </button>
        <div className="menu-wrap">
          <button
            type="button"
            className={`header-btn icon-btn ${menuOpen ? 'active' : ''}`}
            data-testid="toolbar-more"
            title="Menu"
            aria-label="Menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <IconMenu size={18} />
          </button>
          {menuOpen && (
            <div className="menu-card" role="menu">
              <label className="menu-row">
                <span>Theme</span>
                <select
                  data-testid="theme-select"
                  value={theme}
                  onChange={(e) => store.set({ theme: e.target.value as 'light' | 'dark' | 'auto' })}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="auto">Auto</option>
                </select>
              </label>
              <button
                type="button"
                className="menu-item desktop-only"
                data-testid="toggle-objects"
                onClick={() => {
                  toggleObjectsPanel();
                  setMenuOpen(false);
                }}
              >
                {objectsPanelVisible ? 'Hide object list' : 'Show object list'}
              </button>
              <div className="menu-meta">{APP_NAME} v{APP_VERSION}</div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
